import { useState } from "react";
import {
  Command, CommandEmpty, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Check, ChevronsUpDown, Sparkles } from "lucide-react";
import { modelPrice, contextLabel, isRecommended } from "@/lib/format";

export function ModelPicker({ models, value, onChange, testId = "model-picker" }) {
  const [open, setOpen] = useState(false);
  const list = models?.length ? models : [{ id: value, name: value }];
  const selected = list.find((m) => m.id === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid={testId} type="button"
          className="flex h-11 w-full items-center justify-between rounded-xl border border-[#e6e4dd] bg-white px-3.5 text-left text-sm transition-colors hover:border-[#0f172a]">
          <span className="flex min-w-0 items-center gap-2">
            <span className="truncate">{selected?.name || value || "Select a model"}</span>
            {isRecommended(value) && <Sparkles className="h-3.5 w-3.5 flex-shrink-0 text-[#d97706]" />}
          </span>
          <ChevronsUpDown className="h-4 w-4 flex-shrink-0 text-[#94a3b8]" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[--radix-popover-trigger-width] min-w-[320px] border-[#e6e4dd] bg-white p-0 shadow-lg">
        <Command className="bg-white">
          <CommandInput data-testid={`${testId}-search`} placeholder="Search models…" className="h-11" />
          <CommandList className="max-h-72">
            <CommandEmpty>No models found.</CommandEmpty>
            {list.map((m) => {
              const price = modelPrice(m);
              const ctx = contextLabel(m);
              const rec = isRecommended(m.id);
              return (
                <CommandItem key={m.id} value={`${m.name || ""} ${m.id}`}
                  data-testid={`model-item-${m.id}`}
                  onSelect={() => { onChange(m.id); setOpen(false); }}
                  className="flex flex-col items-start gap-1 py-2.5">
                  <div className="flex w-full items-center gap-2">
                    <span className="truncate font-medium">{m.name || m.id}</span>
                    {rec && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-700">
                        <Sparkles className="h-2.5 w-2.5" /> Recommended
                      </span>
                    )}
                    {value === m.id && <Check className="ml-auto h-4 w-4 text-[#059669]" />}
                  </div>
                  <span className="truncate text-xs text-[#94a3b8] font-mono2">{m.id}</span>
                  {(price || ctx) && (
                    <div className="flex flex-wrap gap-x-3 text-xs font-mono2">
                      {price && <span className="text-[#059669]">{price}</span>}
                      {ctx && <span className="text-[#94a3b8]">{ctx}</span>}
                    </div>
                  )}
                </CommandItem>
              );
            })}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
